import React from 'react';
import { cn } from '../../../lib/cn';

/**
 * Avatar, `.dsk-avatar`.
 *
 * Source selector: `system.css` `.dsk-avatar`.
 *
 * Doctrine enforced:
 * - The gradient comes from `--dsk-avatar-from` / `--dsk-avatar-to`, tokenised
 *   precisely because every template was re-inventing the same grey and only
 *   some of them patched it for dark. Nothing is hardcoded here.
 * - An avatar almost always sits next to the name it belongs to, so it is
 *   `aria-hidden` by default and does not make a screen reader say the name
 *   twice. Pass `label` on the rare standalone one and it gets `role="img"`.
 * - Initials only: the vendored CSS has no `__img` sub-part, so there is no
 *   sanctioned way to emit a photo here without inventing a class.
 *
 * `initials` is truncated to two characters, which is what the 32px box holds
 * at `--ds-text-xs` without the text spilling out of the circle.
 */
export const Avatar = React.forwardRef(function Avatar(
  { initials, name, label, className, children, ...rest },
  ref,
) {
  const derived =
    initials ||
    (name
      ? name
          .trim()
          .split(/\s+/)
          .slice(0, 2)
          .map((part) => part[0])
          .join('')
      : '');

  const a11y = label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': 'true' };

  return (
    <div ref={ref} className={cn('dsk-avatar', className)} {...a11y} {...rest}>
      {children || String(derived).slice(0, 2).toUpperCase()}
    </div>
  );
});
Avatar.displayName = 'Avatar';

export default Avatar;
